// Motorul de sincronizare v10.
//
//   base    = ultima stare confirmată de server (din /api/changes)
//   pending = operațiile mele încă netrimise / neconfirmate (coada din IndexedDB)
//   view    = base + pending re-aplicate peste → ce vede utilizatorul
//
// De ce așa: când vine o actualizare de la server, NU pierdem ce am modificat local și nu am trimis
// încă — pur și simplu re-aplicăm operațiile mele peste noua stare. Serverul aplică aceleași operații
// câmp-cu-câmp, deci rezultatul final e identic pe toate dispozitivele.

import type { EntityName, Op, Member, OpResult, ChangesResponse } from '../../shared/types';
import { ENTITY_NAMES, loadAll, saveRows, enqueue, removeFromOutbox, resetLocal } from './local-db';
import { apiJson } from '../lib/http';

type Table = Map<string, any>;
export type Tables = Record<EntityName, Table>;

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'offline' | 'error' | 'unauthorized';
  pending: number;
  lastOk: number | null;
  message?: string;
}

const emptyTables = (): Tables => Object.fromEntries(ENTITY_NAMES.map(e => [e, new Map()])) as Tables;

let base: Tables = emptyTables();
let view: Tables = emptyTables();
let pending: Array<{ seq: number; op: Op }> = [];
let cursor = 0;
let me: Member | null = null;
let status: SyncStatus = { state: 'idle', pending: 0, lastOk: null };
let version = 0;
const listeners = new Set<() => void>();
const errorListeners = new Set<(msg: string) => void>();

export const store = {
  get view() { return view; },
  get me() { return me; },
  get status() { return status; },
  get version() { return version; },
  subscribe(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); },
  onError(fn: (msg: string) => void) { errorListeners.add(fn); return () => errorListeners.delete(fn); }
};

function emit() { version++; listeners.forEach(l => l()); }
function setStatus(s: Partial<SyncStatus>) { status = { ...status, ...s, pending: pending.length }; emit(); }

// ---------- aplicarea locală a operațiilor (oglinda logicii din server/ops.ts) ----------
function cloneTables(t: Tables): Tables {
  const out = {} as Tables;
  for (const e of ENTITY_NAMES) out[e] = new Map(t[e]);
  return out;
}

export function applyOpLocally(t: Tables, op: Op, who: Member | null) {
  const now = new Date().toISOString();
  if (op.type === 'reading.set') {
    const memberId = op.member_id || who?.id || '';
    const id = `${memberId}:${op.work_id}`;
    const prev = t.reading.get(id) || { id, member_id: memberId, work_id: op.work_id, status: 'necitit' };
    t.reading.set(id, { ...prev, ...op.fields, updated_at: now });
    return;
  }
  const table = t[op.entity];
  if (!table) return;
  if (op.type === 'delete') {
    table.delete(op.id);
    if (op.entity === 'works') for (const [cid, c] of t.copies) if (c.work_id === op.id) t.copies.delete(cid);
    return;
  }
  const prev = table.get(op.id);
  if (!prev && op.type === 'create') {
    const defaults: Record<string, unknown> =
      op.entity === 'copies' ? { owner_id: who?.id, household_id: who?.household_id, status: 'acasa', destination: 'pastrez', added_at: now.slice(0, 10) } :
      op.entity === 'works' ? { created_by: who?.id, authors: '', category: 'Diverse' } : {};
    table.set(op.id, { id: op.id, ...defaults, ...op.fields, created_at: now, updated_at: now, _local: true });
  } else if (prev) {
    table.set(op.id, { ...prev, ...op.fields, updated_at: now });
  }
}

function rebuildView() {
  const t = cloneTables(base);
  for (const p of pending) applyOpLocally(t, p.op, me);
  view = t;
  emit();
}

function applyServerRows(rows: ChangesResponse['rows'], full: boolean) {
  if (full) base = emptyTables();
  for (const e of ENTITY_NAMES) {
    for (const r of (rows[e] || []) as any[]) {
      if (r.deleted_at) base[e].delete(r.id);
      else base[e].set(r.id, r);
    }
  }
}

// ---------- API public ----------
export function newId() { return crypto.randomUUID(); }

export async function mutate(ops: Op[]) {
  for (const op of ops) {
    const seq = await enqueue(op);
    pending.push({ seq, op });
  }
  rebuildView();
  setStatus({});
  schedulePush();
}

let pushTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePush() {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => { void sync(); }, 250);
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  return apiJson<T>(path, init);
}

async function push() {
  while (pending.length) {
    const batch = pending.slice(0, 100);
    const { results } = await api<{ results: OpResult[] }>('/api/ops', { method: 'POST', body: JSON.stringify({ ops: batch.map(b => b.op) }) });
    const done: number[] = [];
    results.forEach((r, i) => {
      const item = batch[i];
      if (!item) return;
      // ok sau eroare definitivă (validare/permisiune) → scoatem din coadă; erorile de rețea aruncă înainte
      done.push(item.seq);
      if (!r.ok) errorListeners.forEach(l => l(r.error || 'Operație respinsă de server'));
    });
    await removeFromOutbox(done);
    pending = pending.filter(p => !done.includes(p.seq));
  }
}

async function pull(forceFull = false) {
  const data = await api<ChangesResponse>(`/api/changes?cursor=${forceFull ? 0 : cursor}`);
  applyServerRows(data.rows, data.full);
  cursor = data.cursor;
  me = data.me;
  await saveRows(data.rows, cursor, me, data.full);
}

let syncing: Promise<void> | null = null;
let again = false;
export function sync(opts: { full?: boolean } = {}): Promise<void> {
  if (syncing) { again = true; return syncing; }
  syncing = (async () => {
    setStatus({ state: 'syncing' });
    try {
      do {
        again = false;
        if (pending.length) await push();
        await pull(!!opts.full);
        opts.full = false;
        rebuildView();
      } while (again);
      setStatus({ state: 'idle', lastOk: Date.now(), message: undefined });
    } catch (e) {
      const err = e as any;
      if (err.unauthorized) setStatus({ state: 'unauthorized', message: err.message });
      else if (!navigator.onLine) setStatus({ state: 'offline', message: 'Fără internet — modificările se trimit automat când revine conexiunea' });
      else setStatus({ state: 'error', message: err.message });
    } finally {
      syncing = null;
    }
  })();
  return syncing;
}

export async function init() {
  const local = await loadAll();
  for (const e of ENTITY_NAMES) for (const r of local.rows[e]) base[e].set(r.id, r);
  cursor = local.cursor;
  me = local.me || null;
  pending = local.outbox.map(o => ({ seq: o.seq!, op: o.op }));
  rebuildView();
  setStatus({});

  void sync({ full: cursor === 0 });
  setInterval(() => { if (document.visibilityState === 'visible') void sync(); }, 20000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void sync(); });
  window.addEventListener('online', () => void sync());
}

export async function fullResync() {
  await resetLocal();
  base = emptyTables();
  cursor = 0;
  await sync({ full: true });
}

// pentru teste e2e / depanare din consolă
(globalThis as any).CasaSync = { store, sync, fullResync, mutate, newId, get pending() { return pending.length; } };
